import { Box, Checkbox, Chip, IconButton, Typography, Divider, CircularProgress } from '@mui/material';
import {
  BookmarkBorder,
  Bookmark,
  Edit,
  RemoveCircleOutline,
  ErrorOutline,
} from '@mui/icons-material';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { ThumbnailImage } from '../ThumbnailImage';
import { ReusePlaceholder, DeletedPlaceholder } from '../placeholders';
import { CatalogViewProps } from './types';

const RETIRED_STATUSES = new Set(['CLEARED', 'DELETED']);

/**
 * List/table view for catalog items — horizontal rows with metadata.
 * Shows: thumbnail | title | creators + year | catalog # | visibility | actions
 */
export const CatalogListView = ({
  items,
  isBookmarked,
  toggleBookmark,
  onEdit,
  onRemoveFromCollection,
  selectedCollectionId,
  canEdit,
  browseItemIds,
  browseOwnerIds,
  routePrefix,
  browseTotal,
  browsePageOffset,
  browsePage,
  browseQuery,
  browsePageSize,
  isSelected,
  onToggleSelect,
  selectionMode,
  showInventoryNumber,
  buildItemLink,
}: CatalogViewProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((item, index) => {
        const artworkId = item.catalogItemId;
        const title = item.status === 'CLEARED' ? 'Available for Reuse' : (item.title || 'Untitled');
        const thumbnailUrl = item.coverImageUrl || null;
        const isProcessing =
          item.processingStatus === 'UPLOADING' ||
          item.processingStatus === 'PROCESSING';
        const isFailed = item.processingStatus === 'FAILED';
        const isRetired = RETIRED_STATUSES.has(item.status ?? '');
        // DESTROYED is tracked separately from RETIRED_STATUSES: the real
        // artwork image must still render (destruction is a historical
        // record), but we still want the row dimmed as a visual cue.
        const isDestroyed = item.status === 'DESTROYED';
        const creatorsText = item.creators.length > 0 ? item.creators.join(', ') : '';
        const yearText = item.creationYear ? String(item.creationYear) : '';
        const creatorAndYear = [creatorsText, yearText].filter(Boolean).join(', ');

        const itemLink = buildItemLink
          ? buildItemLink(item)
          : { to: `/object-details/${artworkId}`, routePrefix: '/object-details' };

        return (
          <Box key={`list-${artworkId}`}>
            <Box
              component={Link}
              to={itemLink.to}
              state={browseItemIds ? { itemIds: browseItemIds, currentIndex: index, routePrefix: itemLink.routePrefix || routePrefix || '/object-details', total: browseTotal, pageOffset: browsePageOffset, browsePage, query: browseQuery, pageSize: browsePageSize, itemOwnerIds: browseOwnerIds } : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 1.5 },
                textDecoration: 'none',
                color: 'inherit',
                ...(selectionMode && isSelected?.(artworkId) && {
                  bgcolor: theme.palette.action.selected,
                }),
                ...((isRetired || isDestroyed) && { opacity: 0.6 }),
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              {/* Selection checkbox */}
              {selectionMode && onToggleSelect && (
                <Checkbox
                  size="small"
                  checked={isSelected?.(artworkId) ?? false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleSelect(artworkId);
                  }}
                  sx={{ flexShrink: 0 }}
                />
              )}

              {/* Thumbnail */}
              <Box sx={{
                width: { xs: 50, sm: 60, md: 70 },
                height: { xs: 50, sm: 60, md: 70 },
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {isProcessing ? (
                  <Box
                    data-testid="processing-indicator"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 0.5,
                    }}
                  >
                    <CircularProgress size={20} color="primary" />
                  </Box>
                ) : isFailed ? (
                  <Box
                    data-testid="processing-failed-indicator"
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 0.5,
                    }}
                  >
                    <ErrorOutline sx={{ fontSize: 20, color: 'error.main' }} />
                  </Box>
                ) : isRetired ? (
                  item.status === 'CLEARED' ? (
                    <ReusePlaceholder variant="thumbnail" />
                  ) : (
                    <DeletedPlaceholder variant="thumbnail" />
                  )
                ) : (
                  <ThumbnailImage src={thumbnailUrl} alt={title} />
                )}
              </Box>

              {/* Title + creator/year */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontStyle: 'italic',
                    fontFamily: 'Libre Baskerville, serif',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </Typography>
                {creatorAndYear && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {creatorAndYear}
                  </Typography>
                )}
              </Box>

              {/* Status chip for retired items */}
              {item.status === 'CLEARED' && (
                <Chip label="Reuse" color="success" size="small" sx={{ flexShrink: 0 }} />
              )}
              {item.status === 'DELETED' && (
                <Chip label="Deleted" color="default" size="small" sx={{ flexShrink: 0 }} />
              )}

              {/* Catalog number */}
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: theme.palette.primary.main,
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {showInventoryNumber && item.customCatalogNumber
                  ? `Inv. ${item.customCatalogNumber}`
                  : item.aliCatalogNumber || ''}
              </Typography>

              {/* Visibility icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {item.visibility === 'public' ? (
                  <PublicIcon
                    data-testid="visibility-icon-public"
                    sx={{ fontSize: 18, color: 'success.main' }}
                  />
                ) : (
                  <LockIcon
                    data-testid="visibility-icon-private"
                    sx={{ fontSize: 18, color: 'error.main' }}
                  />
                )}
              </Box>

              {/* Action buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  flexShrink: 0,
                }}
                onClick={(e) => e.preventDefault()}
              >
                <IconButton
                  size="small"
                  aria-label={`Bookmark ${title}`}
                  aria-pressed={isBookmarked(artworkId)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleBookmark(artworkId);
                  }}
                  sx={{
                    color: isBookmarked(artworkId) ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {isBookmarked(artworkId) ? <Bookmark fontSize="small" /> : <BookmarkBorder fontSize="small" />}
                </IconButton>
                {canEdit && (
                  <IconButton
                    size="small"
                    aria-label="edit artwork"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEdit(artworkId);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {canEdit && selectedCollectionId && onRemoveFromCollection && (
                  <IconButton
                    size="small"
                    aria-label="remove from album"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveFromCollection(artworkId, title);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <RemoveCircleOutline fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            {index < items.length - 1 && <Divider />}
          </Box>
        );
      })}
    </Box>
  );
};
